/**
 * V2 Insights — /insights  &  /insights/:slug
 *
 * Public marketing pillar articles. Michael-approved on 2026-07-02 →
 * promoted from /insights-preview (removed noindex, added to sitemap,
 * cross-linked between related articles, added to nav + footer).
 *
 * Voice: plain-English Aussie SMB. Grounded in kindra_brain benchmarks.
 * Every article carries an FAQ section + FAQPage JSON-LD (both prerendered
 * in raw HTML by scripts/prerender_seo.js and mounted at runtime).
 */
/* eslint-disable react/no-unescaped-entities */
import React, { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { V2Layout, Reveal, SpectrumText, BOOK_HREF } from './V2Layout';

const NAVY = '#100838';
const ICY = '#DCF0F5';
const HAIR = 'rgba(220,240,245,0.10)';
const MUTED = 'rgba(220,240,245,0.72)';
const FAINT = 'rgba(220,240,245,0.55)';

const LEAK_COLOR = {
  'Time': '#EB258D',
  'Money': '#9156C4',
  'Lost Work': '#4F8FD0',
  'Pricing': '#1FB6C1',
  'Blind Spots': '#34D1B0',
};

// 8 pillar articles — plain-English Aussie SMB voice, grounded in real
// benchmarks. Kept intentionally direct: TL;DR → 3 sub-headings → FAQ.
const ARTICLES = [
  {
    slug: 'why-quotes-take-forever',
    leak: 'Time',
    title: 'Why your quotes take forever (and how to get most of your evening back)',
    intro: "If you're quoting after dinner, you're not alone — and you're not the problem. The tool is.",
    tldr: "The average Aussie tradie spends 6-10 hours a week on quotes done after paid work. Most of that is chasing photos and re-writing the same line items. Fix the friction, not the person.",
    body: [
      { h: "Where the hours actually go", p: "It's rarely the quoting itself — it's the collecting. Photos on the phone, measurements on a scrap, the client's exact requirement half-remembered from the site visit. By the time you sit down at 8pm, you're rebuilding the job from memory. That's what takes two hours per quote." },
      { h: "The fix isn't a fancier template", p: "It's a job-detail form the client fills before you show up, and a quote skeleton that auto-populates from your on-site photos and voice notes. Same tool most people already have (Tradify, ServiceM8, simPRO) — just wired differently." },
      { h: "What 'sorted' looks like", p: "Site visit ends. You tap 'Draft quote', pick from your saved line items, review on the drive back. Client has the quote before dinner. Your evenings come back." },
    ],
    faqs: [
      { q: 'How long does a fix like this take to build?', a: 'Usually 2-3 weeks depending on which quoting tool you use. If you already have Tradify or ServiceM8, most of the plumbing is there — we just wire it up.' },
      { q: 'Do I need to switch tools?', a: 'Almost never. We connect what you already use. Switching quoting tools is disruptive; connecting them is not.' },
      { q: "What's this actually worth in dollars?", a: 'A crew doing 4 quotes a week saves roughly 6-8 hours a week. At Aussie tradie rates, that\'s $600-$1,200 a week back — before the extra quotes you can now win because you\'re faster.' },
    ],
  },
  {
    slug: 'the-quiet-cost-of-missed-calls',
    leak: 'Lost Work',
    title: "The quiet cost of missed calls (and why voicemail's a dead end)",
    intro: "If you're missing calls, you're not just losing a call — you're losing about 85% of the work behind it.",
    tldr: "In Australian trades, ~85% of callers who reach voicemail never call back (PATLive/Dialzara 2025). A missed-call SMS with a booking link catches most of them.",
    body: [
      { h: "The 85% number, plainly", p: "Someone rings a plumber. They get voicemail. They call the next plumber. That's it — you're not on the callback list. Repeatedly verified across small-business call data since 2022." },
      { h: "The cheapest fix that actually works", p: "A missed-call SMS that goes out inside 30 seconds. Copy that says 'Sorry we missed you — quickest way to book is [link]'. Bookings you'd otherwise never see." },
      { h: "Why SaaS-only isn't the answer", p: "Off-the-shelf tools (Missed-It, OpenPhone) work — but they charge a monthly subscription for something you can own outright on Twilio for cents per SMS. Same result, no ongoing fee." },
    ],
    faqs: [
      { q: "How many missed calls does the average two-person crew get a week?", a: "Enough that fixing this alone often pays for the whole Leak Check. In our audits, 20-40% of after-hours enquiries go to voicemail." },
      { q: "Won't clients find an SMS weird?", a: "The opposite. Most people prefer text to being on hold. As long as the copy reads like a human wrote it, they book." },
    ],
  },
  {
    slug: 'how-to-tell-if-you-re-underpricing',
    leak: 'Pricing',
    title: "How to tell if you're underpricing (without a spreadsheet)",
    intro: "You don't need a full costing model — you need three questions and the answers you already have.",
    tldr: "Most Aussie SMB owners have never priced from a cost base. Three quick checks tell you whether you're leaving 15-30% on the table.",
    body: [
      { h: "Question 1: what's your actual gross margin?", p: "Take last quarter's revenue. Subtract materials, subcontractors, and the direct hours you or your crew put in at real wages. That's your gross. If it's under 40% and you're not sub-scale, you're likely underpricing." },
      { h: "Question 2: do you win too much?", p: "Genuinely. If you win over 70% of quotes, you're the cheapest bid. That's not a compliment. Push prices until you're winning 50-60%." },
      { h: "Question 3: which jobs make you money?", p: "Most owners can't answer this. If you can't split last quarter's profit by job type, you're not underpricing on average — you're underpricing on some and overpricing on others. A floor-price card fixes it in a week." },
    ],
    faqs: [
      { q: 'Won\'t raising prices lose me clients?', a: 'The right clients respond to fair pricing, not the cheapest. In every pricing audit we\'ve done, revenue went up and headache clients dropped away.' },
      { q: 'Where does AI fit into pricing?', a: 'Not the pricing decision — that\'s a business call. But AI can watch your job data and flag quotes that would land below your floor price BEFORE they go out.' },
    ],
  },
  {
    slug: 'where-your-cash-goes-quiet',
    leak: 'Money',
    title: "Where your cash goes quiet — and how to find it",
    intro: "The average Aussie SMB waits 43 days to get paid. That's not a market condition — it's a systems gap.",
    tldr: "AU trades wait ~43 days for payment vs a 24-day national norm (Payment Times Reporting Regulator 2025). Fixing it is faster than raising prices.",
    body: [
      { h: "The 43-day average, in real terms", p: "You did the work in July. You invoiced late-July. You got paid mid-September. That gap is roughly $30,000 of working capital sitting outside your bank account at any moment for a two-person crew." },
      { h: "Why chasing doesn't work", p: "Manual chasing is emotionally expensive and inconsistent. Half the invoices get chased twice, half never get chased. Automation removes the awkward — polite, timed nudges at day 3, 7 and 14." },
      { h: "The build", p: "Xero (or MYOB / QuickBooks) already fires reminders. What most owners don't do is set them up. That's 30 minutes of configuration for months of freed cash flow." },
    ],
    faqs: [
      { q: 'Do the reminders damage relationships?', a: 'Only if the copy sounds robotic. Real Aussie plain-English reminders come across as helpful — most late payers appreciate the nudge because they forgot.' },
      { q: 'What about the client who never pays?', a: 'Automation surfaces them faster. Once you can see who\'s past 30 days on Monday morning without hunting, you decide whether to keep them.' },
    ],
  },
  {
    slug: 'what-a-morning-dashboard-should-show',
    leak: 'Blind Spots',
    title: "What a morning dashboard should show you (and what it shouldn't)",
    intro: "The best owner dashboards fit on one screen and answer three questions. That's it.",
    tldr: "Most SMB owners don't need MORE data — they need three numbers they can trust, delivered before their first coffee.",
    body: [
      { h: "Question 1: what does today look like?", p: "Jobs booked. First one starts at 8am. Two are urgent. That's your day in a sentence — before you open your phone." },
      { h: "Question 2: what's overdue?", p: "Invoices past 30 days. Missed calls from yesterday. Quotes waiting on client response for over a week. Three numbers. If any of them are red, that's your first hour." },
      { h: "Question 3: how did we do last week?", p: "Revenue vs the average. Not vs budget — no one hits budget in trades. Just a rolling number so you know whether the boat's moving." },
    ],
    faqs: [
      { q: 'What tool builds this?', a: 'Anything that reads from your existing stack. We\'ve built dashboards in Retool, Notion, and custom on top of Xero + ServiceM8. Owner picks the shape; we handle the plumbing.' },
      { q: 'Do I need to look at it every day?', a: 'Yes, but for 60 seconds. The whole point is that it fits on one screen and answers three questions. If it takes longer, it\'s the wrong dashboard.' },
    ],
  },
  {
    slug: 'the-real-cost-of-quotes-after-dinner',
    leak: 'Time',
    title: "The real cost of doing quotes after dinner",
    intro: "It's not just the hours. It's the compounding cost across your week, your family, and your win rate.",
    tldr: "Every hour of after-hours quoting costs you double — once in wages you'd never charge a client, once in decision fatigue that lowers your quote quality.",
    body: [
      { h: "The wage tax", p: "You wouldn't charge a client for 10pm hours. So why are you doing them for yourself? Every after-hours quote is billable-rate work being done at zero pay — often at 40% quality." },
      { h: "The decision-fatigue tax", p: "Late-night quotes miss things. Wrong material assumptions. Forgotten line items. Underquoted by mistake because the day was too long. This is where owners lose 5-10% margin — one bad estimate at 11pm at a time." },
      { h: "The family tax", p: "This one doesn't show up on the P&L, but it shows up everywhere else. Fixing the quoting flow gets you two nights a week back — that's what matters." },
    ],
    faqs: [
      { q: "Isn't after-hours work just part of running a business?", a: "For emergencies, yes. For quoting, no. Quoting is a system problem, not a discipline problem." },
    ],
  },
  {
    slug: 'why-follow-ups-fall-through',
    leak: 'Lost Work',
    title: "Why follow-ups fall through the cracks (and the fix)",
    intro: "Most 'lost leads' aren't lost. They're sitting in your inbox, unresponded to, quietly cooling.",
    tldr: "The Marketing Metrics book put the number at 80% of sales requiring 5+ follow-ups. Most Aussie SMBs stop at 1.",
    body: [
      { h: "The gap", p: "You quote. Client says 'thanks, thinking about it.' You never chase. Two weeks later, you assume they went with someone else — but half the time, they were still deciding when someone ELSE followed up with them." },
      { h: "The build", p: "A three-touch sequence: day 3 nudge, day 7 nudge, day 14 close-out. Written like you'd write to a mate. Wired into your quoting tool so it fires automatically after every quote goes out." },
      { h: "Why 'AI-written follow-ups' aren't the answer", p: "Auto-follow-ups only work if they sound like you. That's a 30-minute copy exercise, not an AI feature." },
    ],
    faqs: [
      { q: 'Won\'t clients feel harassed?', a: 'Three polite messages over two weeks is the norm, not harassment. If a client doesn\'t reply after that, they weren\'t going to buy anyway — and you have your answer.' },
      { q: 'Do these follow-ups actually work?', a: 'In our audits, adding a proper follow-up flow lifts quote-to-win rate by 15-30%. It\'s the highest-ROI change we recommend most of the time.' },
    ],
  },
  {
    slug: 'picking-a-price-that-actually-pays',
    leak: 'Pricing',
    title: "Picking a price that actually pays (without becoming an accountant)",
    intro: "You don't need a costing model. You need a floor and a reason.",
    tldr: "Your floor price = (your target hourly rate × job hours) + materials × 1.2. Below that, walk away. Above it, feel free.",
    body: [
      { h: "Set the floor first", p: "For each job type, decide the lowest price you'd accept and still be happy. That's your floor. Not your list price — your floor. Discount below that only for a reason worth putting on paper." },
      { h: "Have a reason for each level", p: "Most owners have one price. Better owners have three: floor (walk-away), fair (their default), and premium (when the job is urgent or complex). Reasons matter more than the numbers — they let you say YES faster." },
      { h: "The build", p: "A floor-price card lives inside your quoting tool. When a quote drops below floor, it flags before it goes out. When it lands above premium, it flags too — because you might be about to lose it." },
    ],
    faqs: [
      { q: "What about competitive pressure?", a: "You will lose some jobs to cheaper quotes. That's the point. Those weren't your clients — they were someone's headache." },
    ],
  },
];

const findArticle = (slug) => ARTICLES.find((a) => a.slug === slug);

// SEO metadata mirrored by scripts/prerender_seo.js so each article's raw
// HTML carries its own title/description/canonical. Keeping the source of
// truth here means the article component and the prerender step never
// drift out of sync (the prerender script imports this file when
// SSR-style access is not available, otherwise falls back to its own copy).
export const INSIGHTS_INDEX_META = {
  title: 'Kindra — Insights · where Aussie SMBs actually leak',
  description: 'Plain-English breakdowns of the eight most common leaks in Australian small businesses — grounded in real benchmarks, written like a mate would explain it.',
  h1: 'Where Aussie SMBs actually leak.',
  intro: 'Plain-English breakdowns of the eight most common leaks we see in Australian small businesses — grounded in real benchmarks, written like a mate would explain it. Every article ends with a real path back to the Free Leak Check.',
};

export const ARTICLE_META = ARTICLES.map((a) => ({
  slug: a.slug,
  title: `Kindra — ${a.title}`,
  description: a.tldr,
  h1: a.title,
  intro: a.intro,
  faqs: a.faqs,
}));

// Related-article cross-links. Curated pairs so navigation feels editorial
// rather than algorithmic. Keys are article slugs; values are 2 related
// slugs shown at the bottom of the article.
const RELATED = {
  'why-quotes-take-forever':          ['the-real-cost-of-quotes-after-dinner', 'why-follow-ups-fall-through'],
  'the-quiet-cost-of-missed-calls':   ['why-follow-ups-fall-through', 'what-a-morning-dashboard-should-show'],
  'how-to-tell-if-you-re-underpricing': ['picking-a-price-that-actually-pays', 'where-your-cash-goes-quiet'],
  'where-your-cash-goes-quiet':       ['how-to-tell-if-you-re-underpricing', 'what-a-morning-dashboard-should-show'],
  'what-a-morning-dashboard-should-show': ['where-your-cash-goes-quiet', 'the-quiet-cost-of-missed-calls'],
  'the-real-cost-of-quotes-after-dinner': ['why-quotes-take-forever', 'why-follow-ups-fall-through'],
  'why-follow-ups-fall-through':      ['the-quiet-cost-of-missed-calls', 'why-quotes-take-forever'],
  'picking-a-price-that-actually-pays': ['how-to-tell-if-you-re-underpricing', 'where-your-cash-goes-quiet'],
};

// FAQPage JSON-LD helper.
const FAQ_JSONLD = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

const CtaBand = () => (
  <section style={{ padding: '48px 24px 72px' }}>
    <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
      <p style={{ fontFamily: 'Inter', color: FAINT, fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase', marginBottom: 12 }}>
        Ready to look at yours?
      </p>
      <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 'clamp(24px,3.5vw,32px)', lineHeight: 1.2, marginBottom: 16 }}>
        Take the <SpectrumText>Leak Check</SpectrumText>.
      </h2>
      <p style={{ fontFamily: 'Inter', fontSize: 15, lineHeight: 1.6, color: MUTED, marginBottom: 24 }}>
        Twenty minutes with Michael. Free. No pitch. You'll walk out with your own leak map and real numbers.
      </p>
      <Link to={`${BOOK_HREF}?src=insights`} data-testid="insights-cta"
            style={{ display: 'inline-block', padding: '14px 32px', borderRadius: 999,
                      background: 'linear-gradient(95deg,#34D1B0 0%,#1FB6C1 25%,#4F8FD0 50%,#9156C4 75%,#EB258D 100%)',
                      color: '#fff', fontFamily: 'Inter', fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
        Book my Leak Check →
      </Link>
      <p style={{ marginTop: 24, fontSize: 13 }}>
        <Link to="/thinking" data-testid="insights-to-thinking"
              style={{ color: FAINT, textDecoration: 'underline', fontFamily: 'Inter' }}>
          See how the firm thinks →
        </Link>
      </p>
    </div>
  </section>
);

export const V2InsightsIndexPage = () => {
  return (
    <V2Layout>
      <div style={{ background: NAVY, minHeight: '100vh', color: ICY }} data-testid="insights-index">
        <section style={{ padding: '64px 24px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <p style={{ fontFamily: 'Inter', color: FAINT, fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase', marginBottom: 14 }}>
              Insights
            </p>
            <h1 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 'clamp(30px,4.5vw,48px)', lineHeight: 1.1, marginBottom: 14 }}>
              Where <SpectrumText>Aussie SMBs</SpectrumText> actually leak.
            </h1>
            <p style={{ fontFamily: 'Inter', fontSize: 17, lineHeight: 1.6, color: MUTED, maxWidth: 720 }}>
              Plain-English breakdowns of the eight most common leaks we see in Australian small businesses — grounded in real benchmarks, written like a mate would explain it.
            </p>
          </div>
        </section>
        <section style={{ padding: '32px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
            {ARTICLES.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`}
                    data-testid={`insights-card-${a.slug}`}
                    style={{ display: 'block', background: '#1A0F44', border: `1px solid ${HAIR}`, borderLeft: `3px solid ${LEAK_COLOR[a.leak]}`,
                              borderRadius: 14, padding: '20px 22px', textDecoration: 'none', color: ICY,
                              transition: 'transform 200ms ease, border-color 200ms ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
                <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                              color: LEAK_COLOR[a.leak], fontWeight: 700, marginBottom: 8 }}>{a.leak}</p>
                <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 17, lineHeight: 1.35, marginBottom: 10 }}>{a.title}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 14, color: MUTED, lineHeight: 1.55 }}>{a.intro}</p>
              </Link>
            ))}
          </div>
        </section>
        <CtaBand />
      </div>
    </V2Layout>
  );
};

export const V2InsightsArticlePage = () => {
  const { slug } = useParams();
  const article = useMemo(() => findArticle(slug), [slug]);
  if (!article) return <Navigate to="/insights" replace />;
  const faqJsonLd = FAQ_JSONLD(article.faqs);
  const relatedSlugs = RELATED[article.slug] || [];
  const relatedArticles = relatedSlugs.map(findArticle).filter(Boolean);
  return (
    <V2Layout>
      <div style={{ background: NAVY, minHeight: '100vh', color: ICY }} data-testid={`insights-article-${article.slug}`}>
        <script type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
        <section style={{ padding: '48px 24px 20px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ maxWidth: 780, margin: '0 auto' }}>
            <Link to="/insights" style={{ fontFamily: 'Inter', fontSize: 12, color: FAINT, textDecoration: 'none' }}>
              ← Back to insights
            </Link>
            <Reveal>
              <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
                            color: LEAK_COLOR[article.leak], fontWeight: 700, marginTop: 20, marginBottom: 12 }}>{article.leak}</p>
              <h1 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 'clamp(28px,4vw,42px)', lineHeight: 1.15, marginBottom: 18 }}>
                {article.title}
              </h1>
              <p style={{ fontFamily: 'Inter', fontSize: 18, lineHeight: 1.6, color: MUTED, marginBottom: 24 }}>{article.intro}</p>
              <div style={{ background: 'linear-gradient(120deg, rgba(31,182,193,0.10), rgba(52,209,176,0.10))',
                              border: `1px solid ${HAIR}`, borderRadius: 12, padding: '16px 20px', marginBottom: 32 }}>
                <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', color: '#5FE0BE', fontWeight: 700, marginBottom: 6 }}>TL;DR</p>
                <p style={{ fontFamily: 'Inter', fontSize: 15, lineHeight: 1.55, color: ICY }}>{article.tldr}</p>
              </div>
            </Reveal>
            </div>
          </div>
        </section>
        <section style={{ padding: '0 24px 32px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {article.body.map((sec, i) => (
              <div key={i} style={{ marginBottom: 28 }}>
                <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 22, marginBottom: 10 }}>{sec.h}</h2>
                <p style={{ fontFamily: 'Inter', fontSize: 16, lineHeight: 1.7, color: MUTED }}>{sec.p}</p>
              </div>
            ))}
            </div>
          </div>
        </section>
        <section style={{ padding: '32px 24px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <h2 style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 22, marginBottom: 16 }}>FAQ</h2>
            {article.faqs.map((f, i) => (
              <div key={i} style={{ background: '#1A0F44', border: `1px solid ${HAIR}`, borderRadius: 12, padding: '16px 20px', marginBottom: 10 }}>
                <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 15, marginBottom: 8, color: ICY }}>{f.q}</p>
                <p style={{ fontFamily: 'Inter', fontSize: 15, color: MUTED, lineHeight: 1.6 }}>{f.a}</p>
              </div>
            ))}
            </div>
          </div>
        </section>
        {relatedArticles.length > 0 && (
          <section style={{ padding: '20px 24px 40px' }} data-testid={`insights-related-${article.slug}`}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
              <div style={{ maxWidth: 720, margin: '0 auto' }}>
              <p style={{ fontFamily: 'Inter', color: FAINT, fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 12 }}>Related reading</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {relatedArticles.map((r) => (
                  <Link key={r.slug} to={`/insights/${r.slug}`}
                        data-testid={`insights-related-link-${r.slug}`}
                        style={{ display: 'block', background: '#1A0F44', border: `1px solid ${HAIR}`,
                                  borderLeft: `3px solid ${LEAK_COLOR[r.leak]}`, borderRadius: 12,
                                  padding: '14px 16px', textDecoration: 'none', color: ICY }}>
                    <p style={{ fontFamily: 'Inter', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: LEAK_COLOR[r.leak], fontWeight: 700, marginBottom: 6 }}>{r.leak}</p>
                    <p style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 14, lineHeight: 1.35 }}>{r.title}</p>
                  </Link>
                ))}
              </div>
              </div>
            </div>
          </section>
        )}
        <CtaBand />
      </div>
    </V2Layout>
  );
};

export default V2InsightsIndexPage;
// Backwards-compat aliases so legacy imports (App.js pre-promotion)
// keep working during rollout. Safe to remove once App.js swap is verified.
export const V2InsightsPreviewIndexPage = V2InsightsIndexPage;
export const V2InsightsPreviewArticlePage = V2InsightsArticlePage;
